import { Link } from "react-router-dom";
import { ArrowLeft, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 py-6">
        <div className="max-w-4xl mx-auto px-6 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10 rounded-sm gap-1">
              <ArrowLeft className="h-4 w-4" /> Torna al sito
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 bg-[#c8e414] rounded-full flex items-center justify-center">
              <Trophy className="h-3.5 w-3.5 text-slate-900" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-lg font-bold text-white uppercase tracking-tight">Tabellone Facile</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900 mb-2">
          Cookie Policy
        </h1>
        <p className="text-sm text-slate-500 mb-10">Ultimo aggiornamento: Aprile 2026</p>

        <div className="prose prose-slate max-w-none space-y-8 text-sm leading-relaxed text-slate-700">

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">1. Cosa sono i cookie</h2>
            <p>
              I cookie sono piccoli file di testo che vengono memorizzati sul dispositivo dell'utente (computer, tablet, smartphone) 
              quando si visita un sito web. I cookie consentono al sito di riconoscere il dispositivo dell'utente e di ricordare 
              alcune informazioni sulle sue preferenze o azioni passate.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">2. Normativa di riferimento</h2>
            <p>
              L'utilizzo dei cookie e disciplinato dal <strong>Regolamento (UE) 2016/679</strong> (GDPR), dal <strong>D.Lgs. 30 giugno 2003, 
              n. 196</strong> (Codice Privacy), dalla <strong>Direttiva 2002/58/CE</strong> (Direttiva ePrivacy) e dalle 
              <strong> Linee guida del Garante per la protezione dei dati personali</strong> del 10 giugno 2021 in materia di cookie 
              e altri strumenti di tracciamento.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">3. Tipologie di cookie utilizzati</h2>

            <div className="space-y-4 mt-3">
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-600"></span>
                  <h3 className="font-bold text-slate-900 text-sm">Cookie tecnici (strettamente necessari)</h3>
                </div>
                <p className="text-slate-600 mb-2">
                  Questi cookie sono essenziali per il funzionamento della Piattaforma e non possono essere disattivati. 
                  Non richiedono il consenso dell'utente ai sensi dell'art. 122, comma 1, del Codice Privacy.
                </p>
                <table className="w-full text-xs border border-slate-100 rounded">
                  <thead><tr className="bg-slate-50"><th className="text-left p-2 font-bold">Cookie</th><th className="text-left p-2 font-bold">Finalita</th><th className="text-left p-2 font-bold">Durata</th></tr></thead>
                  <tbody className="text-slate-600">
                    <tr className="border-t"><td className="p-2 font-mono">session_token</td><td className="p-2">Autenticazione e mantenimento della sessione utente</td><td className="p-2">30 giorni</td></tr>
                    <tr className="border-t"><td className="p-2 font-mono">cookie_consent</td><td className="p-2">Memorizzazione della scelta dell'utente sui cookie</td><td className="p-2">365 giorni</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
                  <h3 className="font-bold text-slate-900 text-sm">Cookie funzionali</h3>
                </div>
                <p className="text-slate-600 mb-2">
                  Questi cookie consentono di migliorare l'esperienza dell'utente, memorizzando preferenze come la lingua o il tema. 
                  Attualmente la Piattaforma non utilizza cookie funzionali aggiuntivi oltre a quelli tecnici.
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
                  <h3 className="font-bold text-slate-900 text-sm">Cookie di profilazione e di terze parti</h3>
                </div>
                <p className="text-slate-600 mb-2">
                  La Piattaforma <strong>non utilizza</strong> cookie di profilazione propri. Tuttavia, i seguenti servizi di terze parti 
                  potrebbero installare cookie propri:
                </p>
                <table className="w-full text-xs border border-slate-100 rounded">
                  <thead><tr className="bg-slate-50"><th className="text-left p-2 font-bold">Servizio</th><th className="text-left p-2 font-bold">Finalita</th><th className="text-left p-2 font-bold">Privacy Policy</th></tr></thead>
                  <tbody className="text-slate-600">
                    <tr className="border-t">
                      <td className="p-2">Google OAuth</td>
                      <td className="p-2">Autenticazione sociale</td>
                      <td className="p-2"><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Link</a></td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-2">Facebook Login</td>
                      <td className="p-2">Autenticazione sociale</td>
                      <td className="p-2"><a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Link</a></td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-2">Stripe</td>
                      <td className="p-2">Elaborazione pagamenti</td>
                      <td className="p-2"><a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Link</a></td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-2">PayPal</td>
                      <td className="p-2">Elaborazione pagamenti</td>
                      <td className="p-2"><a href="https://www.paypal.com/privacy/full" target="_blank" rel="noopener noreferrer" className="text-green-700 underline">Link</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">4. Gestione dei cookie</h2>
            <p>
              Al primo accesso alla Piattaforma viene presentato un banner informativo che consente all'utente di:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>Accettare tutti i cookie</strong></li>
              <li><strong>Accettare solo i cookie necessari</strong> (tecnici)</li>
            </ul>
            <p className="mt-3">
              E inoltre possibile gestire i cookie attraverso le impostazioni del proprio browser. 
              Si noti che la disattivazione dei cookie tecnici potrebbe compromettere il funzionamento della Piattaforma.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mt-3">
              <p className="text-amber-800 text-xs font-medium">
                Istruzioni per la gestione dei cookie nei principali browser:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-xs text-amber-700">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/it/kb/protezione-antitracciamento-avanzata-firefox-desktop" target="_blank" rel="noopener noreferrer" className="underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/it-it/windows/eliminare-e-gestire-i-cookie-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="underline">Microsoft Edge</a></li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">5. Aggiornamenti</h2>
            <p>
              La presente Cookie Policy puo essere aggiornata in qualsiasi momento. Le modifiche saranno pubblicate su questa pagina 
              con indicazione della data di ultimo aggiornamento.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">6. Contatti</h2>
            <p>
              Per qualsiasi domanda relativa all'uso dei cookie, e possibile contattarci all'indirizzo: <strong>privacy@tabellonefacile.net</strong>
            </p>
          </section>
        </div>
      </main>

      <footer className="bg-slate-900 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-xs">Tabellone Facile - Gestione intelligente dei tornei di tennis</p>
        </div>
      </footer>
    </div>
  );
}
